
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { PricingPlan, UserSubscription, UserCredits, UserTokenUsage } from "./types";

// Fetch available pricing plans
export const fetchPlans = async (): Promise<PricingPlan[]> => {
  try {
    console.log("Fetching pricing plans");
    const { data, error } = await supabase
      .from('pricing_plans')
      .select('*')
      .eq('is_active', true)
      .order('price_cents', { ascending: true });
      
    if (error) throw error;
    console.log("Pricing plans fetched:", data);
    return data || [];
  } catch (error: any) {
    console.error("Error fetching pricing plans:", error);
    toast.error("Failed to load pricing plans");
    return [];
  }
};

// Fetch user subscription
export const fetchUserSubscription = async (userId: string): Promise<UserSubscription | null> => {
  try {
    console.log(`Fetching subscription for user: ${userId}`);
    const { data, error } = await supabase
      .from('user_subscriptions')
      .select(`
        id,
        plan_id,
        status,
        current_period_end
      `)
      .eq('user_id', userId)
      .eq('status', 'active')
      .single();
      
    if (error && error.code !== 'PGRST116') {
      console.error("Error fetching subscription:", error);
      throw error;
    }
    
    console.log("Subscription data:", data);
    return data;
  } catch (error: any) {
    console.error("Error fetching user subscription:", error);
    return null;
  }
};

// Fetch user credits
export const fetchUserCredits = async (userId: string): Promise<UserCredits | null> => {
  try {
    console.log(`Fetching credits for user: ${userId}`);
    const { data: creditsData, error: creditsError } = await supabase
      .from('user_credits')
      .select('credits_balance, credits_used, next_reset_at, last_reset_at')
      .eq('user_id', userId)
      .single();
      
    if (creditsError) {
      console.error("Error fetching user credits:", creditsError);
      return null;
    }
    
    console.log("User credits data:", creditsData);
    return creditsData;
  } catch (error: any) {
    console.error("Error fetching user credits:", error);
    return null;
  }
};

// Backward compatibility - alias to fetchUserCredits
export const fetchUserTokenUsage = async (userId: string): Promise<UserTokenUsage | null> => {
  return await fetchUserCredits(userId);
};

// Format price for display
export const formatPrice = (priceCents: number): string => {
  const priceInDollars = priceCents / 100;
  return priceInDollars === 0 ? "Free" : `$${priceInDollars.toFixed(2)}`;
};

// Check if a plan type is a paid plan
export const isPaidPlan = (planType: string | null): boolean => {
  return planType === "monthly" || planType === "yearly";
};

// Handle free subscription
export const handleFreeSubscription = async (userId: string, planId: string): Promise<boolean> => {
  try {
    // Check if user already has a subscription
    const { data: existingSubscription, error: fetchError } = await supabase
      .from('user_subscriptions')
      .select('*')
      .eq('user_id', userId)
      .single();

    if (fetchError && fetchError.code !== 'PGRST116') throw fetchError;

    if (existingSubscription) {
      // Update existing subscription to free plan
      const { error: updateError } = await supabase
        .from('user_subscriptions')
        .update({ 
          plan_id: planId,
          status: 'active',
          current_period_end: null,
          updated_at: new Date().toISOString()
        })
        .eq('id', existingSubscription.id);
        
      if (updateError) throw updateError;
    } else {
      // Create new subscription with free plan
      const { error: insertError } = await supabase
        .from('user_subscriptions')
        .insert({
          user_id: userId,
          plan_id: planId,
          status: 'active'
        });
        
      if (insertError) throw insertError;
    }

    return true;
  } catch (error: any) {
    console.error("Error handling free subscription:", error);
    toast.error("Failed to update subscription");
    return false;
  }
};

// Initiate Stripe checkout
export const initiateStripeCheckout = async (userId: string, planId: string, promoCode?: string) => {
  try {
    console.log(`Initiating checkout for user ${userId} with plan ${planId}`);
    const { data: plan, error: planError } = await supabase
      .from('pricing_plans')
      .select('*')
      .eq('id', planId)
      .single();
      
    if (planError) throw planError;
    
    if (!plan.stripe_price_id) {
      throw new Error(`No Stripe price ID configured for plan: ${plan.name}`);
    }
    
    console.log(`Using stripe price ID: ${plan.stripe_price_id}`);
    const session = await supabase.functions.invoke('create-checkout', {
      body: {
        planId: planId,
        userId: userId,
        promoCode: promoCode
      }
    });
    
    if (!session.data || session.error) {
      console.error("Checkout session error:", session.error || "No data returned");
      throw new Error(session.error?.message || "Failed to create checkout session");
    }
    
    console.log("Checkout session created:", session.data);
    return { 
      success: true, 
      url: session.data.url 
    };
  } catch (error: any) {
    console.error("Error initiating Stripe checkout:", error);
    return { 
      success: false, 
      error: error.message || 'Failed to initiate checkout'
    };
  }
};

// Check if we're in test mode
export const checkTestMode = (plans: PricingPlan[]): boolean => {
  // Check if any plan has test-mode Stripe product IDs (the ones we set for test mode)
  const testProductIds = ["prod_SAOZCj6KM6o0rN", "prod_SAOaxixWXcJzFs"];
  const hasTestProductIds = plans.some(plan => 
    testProductIds.includes(plan.stripe_product_id || '')
  );
  
  console.log(`Test mode detected: ${hasTestProductIds}`);
  return hasTestProductIds;
};

// Update Stripe Product ID
export const updateStripeProductId = async () => {
  try {
    const { error: yearlyPlanError } = await supabase
      .from('pricing_plans')
      .update({ stripe_product_id: 'prod_SAOaxixWXcJzFs' })
      .eq('type', 'yearly');

    if (yearlyPlanError) throw yearlyPlanError;

    toast.success("Stripe Product ID updated successfully");
    return true;
  } catch (error: any) {
    console.error("Error updating Stripe Product ID:", error);
    toast.error("Failed to update Stripe Product ID");
    return false;
  }
};

// New helper function to check if a user has a paid subscription
export const checkIfUserHasPaidPlan = async (userId: string): Promise<boolean> => {
  if (!userId) return false;
  
  try {
    const subscription = await fetchUserSubscription(userId);
    if (!subscription) return false;
    
    // Get the plan type based on the plan_id
    const { data: planData, error: planError } = await supabase
      .from('pricing_plans')
      .select('type')
      .eq('id', subscription.plan_id)
      .single();
      
    if (planError) return false;
    
    const planType = planData.type;
    return isPaidPlan(planType);
  } catch (error) {
    console.error("Error checking if user has paid plan:", error);
    return false;
  }
};
